import { useShopStatusStore } from "@/abilities/shop/features/shop-core/store/shop-status.store";
import { Sap } from "@/abilities/shop/types/sap-shop";
import { getAuthHeader } from "@/shared/authentication/authentication";
import { publicClient } from "@/shared/services/apollo-clients/public-client";
import { decodeJwtPayload } from "@/shared/utils/jwt/token-parser";
import { safeJoinURL } from "@/shared/utils/route-utils";
import { get, set } from "@vueuse/core";
import { gql } from "graphql-tag";
import { ref } from "vue";

// Create a singleton token store that persists across component lifecycles
const jwtToken = ref<string | undefined>(undefined);

export function useSapAuth() {
    const setJwtToken = (token: string) => {
        set(jwtToken, token);
    };

    const getJwtToken = (): string | undefined => {
        return get(jwtToken);
    };

    const getTokenDetails = (): Sap.Token | undefined => {
        if (jwtToken.value) {
            const decoded = decodeJwtPayload(jwtToken.value);
            if (!decoded) return undefined;
            return {
                accessToken: (decoded.accessToken as string | undefined) ?? "",
            };
        } else {
            return undefined;
        }
    };

    const getAccessToken = (): string | undefined => {
        return getTokenDetails()?.accessToken;
    };

    const getTenantId = async (): Promise<string | undefined> => {
        try {
            const { data } = await publicClient.query({
                query: gql`
                    query tenantId {
                        tenant {
                            id
                        }
                    }
                `,
            });
            return data.tenant.id;
        } catch (error) {
            console.error("Failed to get tenant ID:", error);
            return undefined;
        }
    };

    const connect = async (): Promise<void> => {
        const tenantId = await getTenantId();
        if (!tenantId) {
            return;
        }

        // Save the current route as the entry path for later redirect
        const store = useShopStatusStore();
        const callbackCurrentRouteAsString = window.location.pathname + window.location.search;
        store.setAppEntryPath(callbackCurrentRouteAsString);

        const { shopServiceUrl } = store.sapConfig() ?? {};

        if (!shopServiceUrl) {
            console.error("No shop service url found");
            return;
        }

        // Redirect to shop service url with sap login endpoint for tenant
        const loginUrl = safeJoinURL(shopServiceUrl, `sap/login/for/${tenantId}`);

        const tokenResponse = await fetch(loginUrl, {
            headers: { Authorization: getAuthHeader() },
        });
        const response = await tokenResponse.json();
        jwtToken.value = response.sapShopToken;
    };

    return {
        connect,
        getAccessToken,
        getJwtToken,
        setJwtToken,
    };
}
